import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Paper,
  Stack,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Logo from "../../Images/logo.png";
import { useNavigate } from "react-router-dom";

const Header = () => {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const avatarRef = useRef(null);

  useEffect(() => {
    const storedUsername = localStorage.getItem("username");
    const storedAccessToken = localStorage.getItem("AccessToken");

    if (storedUsername) setUsername(storedUsername);
    if (storedAccessToken) setAccessToken(storedAccessToken);
  }, []);

  const toggleDrawer = (open) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift"))
      return;
    setDrawerOpen(open);
  };

  const getUserInitial = () => {
    return username ? username.charAt(0).toUpperCase() : "U";
  };

  const handleAvatarClick = (event) => {
    setMenuOpen(!menuOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem("username");
    localStorage.removeItem("userId");
    localStorage.removeItem("selectedImageUrl");
    setUsername("");
    navigate(`/${accessToken}/login`);
    setMenuOpen(false);
  };

  return (
    <>
      <style>
        {`
          /* Ensure body doesn't shift when dropdown is open */
          body {
            overflow-x: hidden;
          }

          /* Header-specific AppBar styles */
          .header-appBar {
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            max-width: 95%;
            width: 100%;
            height: 60px;
            background-color: rgba(60, 60, 60, 0.8);
            border-radius: 30px;
            box-shadow: none;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .header-toolbar {
            min-height: 80px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding: 0 16px;
          }

          /* Header-specific Logo styles */
          .header-logo img {
            height: 40px;
            width: 40px;
            cursor: pointer;
            margin-left: 0;
          }

          /* Header-specific Navigation menu styles */
          .header-nav-menu {
            display: flex;
            justify-content: center;
            align-items: center;
            flex-grow: 1;
            gap: 16px;
          }

          .header-button {
            color: white;
            font-size: 16px;
            text-transform: none;
            font-weight: bold;
            font-family: "niavhFont", sans-serif;
          }

          .header-button:hover {
            background-color: transparent;
            text-decoration: none;
          }

          /* Header-specific User section styles */
          .header-user-section {
            display: flex;
            align-items: center;
            margin-left: auto;
          }

          .header-avatar {
            width: 36px;
            height: 36px;
            background-color: #e91e63;
            font-family: "niavhFont", sans-serif;
            font-weight: bold;
            cursor: pointer;
          }

          /* Header-specific Mobile Menu styles */
          .header-drawer-paper {
            background-color: #272727;
            height: 100vh;
            color: white;
            text-align: center;
          }

          .header-listItem {
            justify-content: center;
            cursor: pointer;
          }

          .header-listItemText {
            color: white;
          }

          /* Header-specific Custom dropdown styles */
          .header-custom-dropdown {
            position: absolute;
            top: 100%;
            right: 0;
            width: 150px;
            margin-top: 8px;
            background-color: #272727;
            color: white;
            border-radius: 8px;
            box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);
            z-index: 1000;
            padding: 4px 0;
          }

          .header-custom-dropdown button {
            width: 100%;
            text-align: left;
            padding: 8px 16px;
            color: white;
            background-color: #ff0000;
            border: none;
            border-radius: 4px;
            font-family: "niavhFont", sans-serif;
            font-weight: bold;
            text-transform: none;
            cursor: pointer;
          }

          .header-custom-dropdown button:hover {
            background-color: #cc0000;
          }

          /* Responsive adjustments */
          @media (min-width: 960px) {
            .header-appBar {
              max-width: 90%;
            }
          }

          @media (min-width: 600px) {
            .header-appBar {
              max-width: 80%;
            }
          }

          @media (max-width: 600px) {
            .header-nav-menu {
              display: none;
            }
          }
        `}
      </style>

      <AppBar className="header-appBar" sx={{ transition: "all 0.3s ease" }}>
        <Toolbar className="header-toolbar">
          {/* Logo */}
          <Box className="header-logo">
            <img
              src={Logo}
              alt="Logo"
              onClick={() => navigate(`/${accessToken}/`)}
            />
          </Box>

          {/* Desktop Navigation */}
          <Box className="header-nav-menu" sx={{ display: { xs: "none", md: "flex" } }}>
            {["lipstick", "eyeliner", "blush", "hair", "eyeshadow", "recommend"].map(
              (item, index) => (
                <Button
                  key={index}
                  className="header-button"
                  onClick={() => navigate(`/${accessToken}/${item}`)}
                >
                  {item}
                </Button>
              )
            )}
          </Box>

          {/* Signup/Login or Avatar */}
          <Box className="header-user-section">
            {username ? (
              <Avatar
                ref={avatarRef}
                className="header-avatar"
                onClick={handleAvatarClick}
              >
                {getUserInitial()}
              </Avatar>
            ) : (
              <Button
                className="header-button"
                onClick={() => navigate(`/${accessToken}/login`)}
              >
                Signup/Login
              </Button>
            )}

            {/* Custom Dropdown Menu */}
            {menuOpen && (
              <Paper className="header-custom-dropdown">
                <Stack>
                  <Button
                    fullWidth
                    onClick={handleLogout}
                    sx={{
                      color: "white",
                      py: 1,
                      textAlign: "center",
                      fontFamily: "niavhFont",
                      texttransform: "none",
                      fontweight: "bold",
                      backgroundColor: "#ff0000",
                      "&:hover": {
                        backgroundColor: "#cc0000",
                      },
                    }}
                  >
                    Logout
                  </Button>
                </Stack>
              </Paper>
            )}
          </Box>

          {/* Mobile Menu */}
          <IconButton
            sx={{ display: { xs: "block", md: "none" }, color: "white" }}
            onClick={toggleDrawer(true)}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar >

      {/* Mobile Drawer */}
      < Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{ className: "header-drawer-paper" }
        }
      >
        <List>
          {username && (
            <ListItem className="header-listItem">
              <Avatar className="header-avatar">{getUserInitial()}</Avatar>
            </ListItem>
          )}

          {["lipstick", "eyeliner", "blush", "hair", "recommend"].map(
            (text, index) => (
              <ListItem
                button
                key={index}
                className="header-listItem"
                onClick={() => {
                  navigate(`/${accessToken}/${text}`);
                  setDrawerOpen(false);
                  toggleDrawer(false);
                }}
              >
                <ListItemText
                  primary={text}
                  classes={{ root: "header-listItemText" }}
                />
              </ListItem>
            )
          )}

          {username ? (
            <ListItem
              button
              className="header-listItem"
              onClick={() => {
                toggleDrawer(false);
                setDrawerOpen(false);
                handleLogout();
              }}
              sx={{ width: "80%", borderRadius: "4px" }}
            >
              <ListItemText
                primary="logout"
                classes={{ root: "header-listItemText" }}
                primaryTypographyProps={{ fontFamily: "niavhFont" }}
              />
            </ListItem>
          ) : (
            <ListItem
              button
              className="header-listItem"
              onClick={() => {
                navigate(`/${accessToken}/login`);
                setDrawerOpen(false);
                toggleDrawer(false);
              }}
            >
              <ListItemText primary={"signup/Login"} />
            </ListItem>
          )}
        </List>
      </Drawer >
    </>
  );
};

export default Header;